import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ModeOutlinedIcon from '@mui/icons-material/ModeOutlined';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { MenuItem, Stack, Typography, useTheme } from '@mui/material';
import { ICellRendererParams } from 'ag-grid-community';
import { MouseEvent, useCallback, useState } from 'react';
import { ConfirmDeletionDialog } from '../../../../components/Dialog/ConfirmDialog/ConfirmDeletionDialog';
import { MenuItemWithDivider, MoreOptionsMenu } from '../../../../components/MoreOptionsMenu/MoreOptionsMenu';
import { FileIngestionConfig } from '../../../../services/queries/FileIngestionQueries';
import {
  useActivateFieldAutomationConfig,
  useDeleteFieldAutomationConfig,
} from '../state/field-automation-state';

interface ActionsCellRendererParams extends ICellRendererParams<FileIngestionConfig> {
  onEdit: (config: FileIngestionConfig) => void;
}

export function ActionsCellRenderer(params: ActionsCellRendererParams) {
  const { data, onEdit } = params;
  const { colors } = useTheme();
  const { mutate: deleteConfig } = useDeleteFieldAutomationConfig();
  const { mutate: activateConfig } = useActivateFieldAutomationConfig();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const handleCloseDeleteModal = () => setOpenDeleteModal(false);

  const handleDelete = () => {
    if (!data) {
      return;
    }
    deleteConfig(data.id);
    handleCloseDeleteModal();
  };

  const handleEdit = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (data) {
        onEdit(data);
      }
    },
    [data, onEdit]
  );

  const handleToggleActive = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (data) {
        activateConfig(data.id);
      }
    },
    [data, activateConfig]
  );

  if (!data) {
    return null;
  }

  return (
    <Stack
      display='grid'
      gridAutoFlow={'column'}
      alignItems='center'
      justifyItems={'center'}
      className='field-automation-menu'
      px={3}
    >
      <ConfirmDeletionDialog
        open={openDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDelete}
        title={`Delete Configuration "${data?.displayName}"?`}
      >
        <Typography variant='body2' color={'text.secondary'} sx={{ pt: '.25rem', minWidth: '30rem' }}>
          Deleting this configuration will deactivate automatic file ingestion for this file type. The
          configuration can be recreated if needed.
        </Typography>
      </ConfirmDeletionDialog>

      {!openDeleteModal && (
        <MoreOptionsMenu style={{ justifySelf: 'end' }}>
          <MenuItemWithDivider
            onClick={handleEdit}
            sx={{ display: 'flex', justifyItems: 'space-between', p: '.5rem' }}
          >
            <ModeOutlinedIcon sx={{ color: colors.neutral['60'], mr: '.25rem' }} />
            <Typography variant='body2' color={colors.primary[100]}>
              Edit
            </Typography>
          </MenuItemWithDivider>
          <MenuItemWithDivider
            onClick={handleToggleActive}
            sx={{ display: 'flex', justifyItems: 'space-between', p: '.5rem' }}
          >
            <PowerSettingsNewIcon sx={{ color: colors.neutral['60'], mr: '.25rem' }} />
            <Typography variant='body2' color={colors.primary[100]}>
              {data.active ? 'Deactivate' : 'Activate'}
            </Typography>
          </MenuItemWithDivider>
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              setOpenDeleteModal(true);
            }}
            sx={{ display: 'flex', justifyItems: 'space-between', p: '.5rem' }}
          >
            <DeleteOutlinedIcon sx={{ color: colors.neutral['60'], mr: '.25rem' }} />
            <Typography variant='body2' color={colors.primary[100]}>
              Delete
            </Typography>
          </MenuItem>
        </MoreOptionsMenu>
      )}
    </Stack>
  );
}
